import { time } from 'echarts';

import { getThemeColor } from '~/design-tokens/themes/theme-utils';
import { DesignToken, ThemeName } from '~/design-tokens/themes/themes';

interface CalendarVirtualData {
  xAxis: string[];
  series: number[];
}

export const getCalendarMockData = (year: string) => {
  const date = +time.parse(year + '-01-01');
  const end = +time.parse(+year + 1 + '-01-01');
  const dayTime = 3600 * 24 * 1000;
  const data: CalendarVirtualData = {
    xAxis: [],
    series: [],
  };

  for (let timeDate = date; timeDate < end; timeDate += dayTime) {
    data.xAxis.push(time.format(timeDate, '{yyyy}-{MM}-{dd}', false));
    data.series.push(Math.floor(Math.random() * 10000));
  }

  return {
    xAxis: data.xAxis,
    series: {
      name: year,
      data: data.series,
    },
  };
};

interface GenerateRangeArrayArgs {
  min: number;
  max: number;
  arrayLength: number;
}

export const generateRangeArray = ({
  min,
  max,
  arrayLength,
}: GenerateRangeArrayArgs) => {
  if (arrayLength === 1) {
    return [min];
  }

  const step = (max - min) / (arrayLength - 1);
  return Array.from({ length: arrayLength }, (_, i) =>
    i === arrayLength - 1 ? max : Math.round(min + step * i)
  );
};

interface Piece {
  min: number;
  max?: number;
  color: string;
}

interface GeneratePiecesArgs {
  theme: ThemeName;
  ranges: number[];
}

const heatmapColors: DesignToken[] = [
  '--chart-multi-lines-hex-1',
  '--chart-multi-lines-hex-2',
  '--chart-multi-lines-hex-3',
  '--chart-multi-lines-hex-4',
  '--chart-multi-lines-hex-5',
];

export const generatePieces = ({ theme, ranges }: GeneratePiecesArgs) => {
  return ranges.reduce<Piece[]>((result, current, index) => {
    const piece = {
      min: index === 0 ? 0 : ranges[index - 1] + 1,
      max: index < ranges.length - 1 ? current : undefined,
      color: getThemeColor(heatmapColors[index % heatmapColors.length], theme),
    };
    result.push(piece);
    return result;
  }, []);
};

export const heatmapProps = {
  'apache-heatmap': {
    xAxis: [
      '2024-04-01',
      '2024-04-02',
      '2024-04-03',
      '2024-04-04',
      '2024-04-05',
      '2024-04-06',
      '2024-04-07',
    ],
    yAxis: [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ],
    series: {
      name: 'META:NASD',
      data: [
        [0, 0, 5],
        [1, 0, 1],
        [2, 0, '-'],
        [3, 0, '-'],
        [4, 0, '-'],
        [5, 0, '-'],
        [6, 0, '-'],
        [7, 0, '-'],
        [8, 0, '-'],
        [9, 0, '-'],
        [10, 0, '-'],
        [11, 0, 2],
        [12, 0, 4],
        [13, 0, 1],
        [14, 0, 1],
        [15, 0, 3],
        [16, 0, 4],
        [17, 0, 6],
        [18, 0, 4],
        [19, 0, 4],
        [20, 0, 3],
        [21, 0, 3],
        [22, 0, 2],
        [23, 0, 5],
        [0, 1, 7],
        [1, 1, '-'],
        [2, 1, '-'],
        [3, 1, '-'],
        [4, 1, '-'],
        [5, 1, '-'],
        [6, 1, '-'],
        [7, 1, '-'],
        [8, 1, '-'],
        [9, 1, '-'],
        [10, 1, 5],
        [11, 1, 2],
        [12, 1, 2],
        [13, 1, 6],
        [14, 1, 9],
        [15, 1, 11],
        [16, 1, 6],
        [17, 1, 7],
        [18, 1, 8],
        [19, 1, 12],
        [20, 1, 5],
        [21, 1, 5],
        [22, 1, 7],
        [23, 1, 2],
        [0, 2, 1],
        [1, 2, 1],
        [2, 2, '-'],
        [3, 2, '-'],
        [4, 2, '-'],
        [5, 2, '-'],
        [6, 2, '-'],
        [7, 2, '-'],
        [8, 2, '-'],
        [9, 2, '-'],
        [10, 2, 3],
        [11, 2, 2],
        [12, 2, 1],
        [13, 2, 9],
        [14, 2, 8],
        [15, 2, 10],
        [16, 2, 6],
        [17, 2, 5],
        [18, 2, 5],
        [19, 2, 5],
        [20, 2, 7],
        [21, 2, 4],
        [22, 2, 2],
        [23, 2, 4],
        [0, 3, 7],
        [1, 3, 3],
        [2, 3, '-'],
        [3, 3, '-'],
        [4, 3, '-'],
        [5, 3, '-'],
        [6, 3, '-'],
        [7, 3, '-'],
        [8, 3, 1],
        [9, 3, '-'],
        [10, 3, 5],
        [11, 3, 4],
        [12, 3, 7],
        [13, 3, 14],
        [14, 3, 13],
        [15, 3, 12],
        [16, 3, 9],
        [17, 3, 5],
        [18, 3, 5],
        [19, 3, 10],
        [20, 3, 6],
        [21, 3, 4],
        [22, 3, 4],
        [23, 3, 1],
        [0, 4, 1],
        [1, 4, 3],
        [2, 4, '-'],
        [3, 4, '-'],
        [4, 4, '-'],
        [5, 4, 1],
        [6, 4, '-'],
        [7, 4, '-'],
        [8, 4, '-'],
        [9, 4, 2],
        [10, 4, 4],
        [11, 4, 4],
        [12, 4, 2],
        [13, 4, 4],
        [14, 4, 4],
        [15, 4, 14],
        [16, 4, 12],
        [17, 4, 1],
        [18, 4, 8],
        [19, 4, 5],
        [20, 4, 3],
        [21, 4, 7],
        [22, 4, 3],
        [23, 4, '-'],
        [0, 5, 2],
        [1, 5, 1],
        [2, 5, '-'],
        [3, 5, 3],
        [4, 5, '-'],
        [5, 5, '-'],
        [6, 5, '-'],
        [7, 5, '-'],
        [8, 5, 2],
        [9, 5, '-'],
        [10, 5, 4],
        [11, 5, 1],
        [12, 5, 5],
        [13, 5, 10],
        [14, 5, 5],
        [15, 5, 7],
        [16, 5, 11],
        [17, 5, 6],
        [18, 5, '-'],
        [19, 5, 5],
        [20, 5, 3],
        [21, 5, 4],
        [22, 5, 2],
        [23, 5, '-'],
        [0, 6, 1],
        [1, 6, '-'],
        [2, 6, '-'],
        [3, 6, '-'],
        [4, 6, '-'],
        [5, 6, '-'],
        [6, 6, '-'],
        [7, 6, '-'],
        [8, 6, '-'],
        [9, 6, '-'],
        [10, 6, 1],
        [11, 6, '-'],
        [12, 6, 2],
        [13, 6, 1],
        [14, 6, 3],
        [15, 6, 4],
        [16, 6, '-'],
        [17, 6, '-'],
        [18, 6, '-'],
        [19, 6, '-'],
        [20, 6, 1],
        [21, 6, 2],
        [22, 6, 2],
        [23, 6, 6],
      ],
    },
  },
  'apache-heatmap-calendar': {
    ...getCalendarMockData('2025'),
    ranges: [499, 1999, 4999, 7999, 8000],
  },
};

export const heatmapJsonSchemas = {
  'apache-heatmap': {
    type: 'object',
    required: ['chartType', 'xAxis', 'yAxis', 'series'],
    properties: {
      chartType: {
        type: 'string',
      },
      xAxis: {
        type: 'array',
        items: {
          type: ['string', 'number'],
        },
      },
      yAxis: {
        type: 'array',
        items: {
          type: ['string', 'number'],
        },
      },
      ranges: {
        type: 'array',
        items: {
          type: 'number',
        },
      },
      series: {
        type: 'object',
        required: ['name', 'data'],
        properties: {
          name: {
            type: 'string',
          },
          data: {
            type: 'array',
            items: {
              type: 'array',
              items: {
                type: 'number',
              },
            },
          },
        },
      },
    },
  },
  'apache-heatmap-calendar': {
    type: 'object',
    required: ['chartType', 'xAxis', 'series'],
    properties: {
      chartType: {
        type: 'string',
      },
      xAxis: {
        type: 'array',
        items: {
          type: ['string', 'number'],
        },
      },
      yAxis: {
        type: 'array',
        items: {
          type: ['string', 'number'],
        },
      },
      ranges: {
        type: 'array',
        items: {
          type: 'number',
        },
      },
      series: {
        type: 'object',
        required: ['name', 'data'],
        properties: {
          name: {
            type: 'string',
          },
          data: {
            type: 'array',
            items: {
              type: 'number',
            },
          },
        },
      },
    },
  },
};
